import React from 'react';
import { Search, Sparkles } from 'lucide-react';

interface SearchFormProps {
  onSearch: (query: string) => Promise<void>;
  isSearching: boolean;
  error?: string;
}

export function SearchForm({ onSearch, isSearching, error }: SearchFormProps) {
  const [topic, setTopic] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim()) return;
    onSearch(topic);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setTopic('');
    }
  };

  return (
    <div className="max-w-3xl mx-auto text-center mb-16">
      <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
        Discover Educational Videos<br />
        <span className="text-indigo-600 dark:text-indigo-400">That Inspire Learning</span>
      </h2>
      <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
        Access high-quality educational content from trusted sources like Khan Academy, 
        Crash Course, and more. Start your learning journey today.
      </p>
      
      <form onSubmit={handleSubmit} className="flex items-center gap-3 max-w-2xl mx-auto">
        <div className="relative flex-1">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a topic (e.g., 'quantum physics', 'world war 2')"
            className="w-full px-6 py-4 rounded-xl border-2 border-indigo-100 dark:border-gray-700 
              focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm 
              text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 
              bg-white dark:bg-gray-800 transition-colors"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
        </div>
        <button
          type="submit"
          disabled={isSearching || !topic.trim()}
          className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 
            text-white px-8 py-4 rounded-xl flex items-center gap-2 transition-all shadow-lg 
            hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed font-medium min-w-[160px]
            justify-center"
        >
          {isSearching ? (
            <>
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              <span>Search</span>
            </>
          )}
        </button>
      </form>

      {error && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg inline-block">
          {error}
        </div>
      )}
    </div>
  );
}
